<template>
  <div class="settlement-ops" data-testid="settlement-ops-page">
    <header class="page-bar">
      <h1>{{ $t('배달 매출 운영') }}</h1>
      <el-button size="small" icon="el-icon-back" @click="$router.push('/v/business/integrated-sales')">
        {{ $t('대시보드') }}
      </el-button>
    </header>

    <section class="ops-grid">
      <div class="ops-panel">
        <h2>{{ $t('백필 실행') }}</h2>
        <el-form label-position="top" size="small" @submit.native.prevent>
          <el-form-item :label="$t('매장')">
            <!-- 매장 코드를 외워 치던 자리 — 범위 트리에서 고른다(§1-6 [E]) -->
            <StorePicker v-model="backfill.storeCd" clearable fallback="input" size="small" />
          </el-form-item>
          <el-form-item :label="$t('처리 건수')">
            <el-input-number v-model="backfill.limit" :min="1" :max="500"></el-input-number>
          </el-form-item>
          <el-form-item :label="$t('백필 기간')">
            <el-date-picker
              v-model="backfillRange"
              type="daterange"
              size="small"
              value-format="yyyy-MM-dd"
              :range-separator="$t('~')"
              :start-placeholder="$t('시작일')"
              :end-placeholder="$t('종료일')">
            </el-date-picker>
          </el-form-item>
          <el-button
            data-testid="settlement-backfill-run"
            type="primary"
            icon="el-icon-refresh"
            :loading="running"
            @click="runBackfill">
            {{ $t('실행') }}
          </el-button>
        </el-form>
        <pre v-if="lastResult" data-testid="settlement-backfill-result">{{ lastResult }}</pre>
      </div>

      <div class="ops-panel">
        <h2>{{ $t('캐시') }}</h2>
        <el-button size="small" icon="el-icon-delete" @click="evictCache">{{ $t('수동 무효화') }}</el-button>
      </div>

      <div class="ops-panel">
        <h2>{{ $t('수수료 매핑 등록') }}</h2>
        <el-form label-position="top" size="small" @submit.native.prevent>
          <el-form-item :label="$t('플랫폼')">
            <el-select v-model="mappingForm.platform" placeholder="Platform">
              <el-option
                v-for="p in deliveryPlatformGroups"
                :key="p"
                :label="platformDisplayName(p)"
                :value="p"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('원본 라벨')">
            <el-input v-model="mappingForm.rawLabel"></el-input>
          </el-form-item>
          <el-form-item :label="$t('표준 항목')">
            <el-select v-model="mappingForm.standardCategory" placeholder="Category">
              <el-option
                v-for="category in standardCategories"
                :key="category"
                :label="category"
                :value="category"></el-option>
            </el-select>
          </el-form-item>
          <el-button type="primary" icon="el-icon-check" @click="createMapping">{{ $t('등록') }}</el-button>
        </el-form>
      </div>
    </section>

    <el-alert v-if="errorMessage" type="error" :closable="false" show-icon class="state-alert">
      <span slot="title">{{ errorMessage }}</span>
    </el-alert>

    <div v-if="loading" class="state-panel" aria-busy="true">
      <i class="el-icon-loading"></i>
      <span>{{ $t('운영 데이터를 불러오는 중입니다') }}</span>
    </div>

    <section class="ops-table">
      <header>
        <h2>{{ $t('매핑 BACKLOG') }}</h2>
        <el-button size="small" icon="el-icon-refresh" @click="loadOps">{{ $t('새로고침') }}</el-button>
      </header>
      <el-table :data="backlog" size="small" border :empty-text="$t('데이타 없음')" max-height="260">
        <el-table-column prop="platform" :label="$t('플랫폼')" width="130"></el-table-column>
        <el-table-column prop="rawLabel" :label="$t('원본 라벨')" min-width="220"></el-table-column>
        <el-table-column prop="sampleFormation" :label="$t('샘플')" min-width="160"></el-table-column>
        <el-table-column :label="$t('표준 항목')" min-width="180">
          <template slot-scope="scope">
            <el-select v-model="resolveCategories[scope.row.id]" size="mini" placeholder="Category">
              <el-option
                v-for="category in standardCategories"
                :key="category"
                :label="category"
                :value="category"></el-option>
            </el-select>
          </template>
        </el-table-column>
        <el-table-column :label="$t('처리')" width="90" align="center">
          <template slot-scope="scope">
            <el-button size="mini" icon="el-icon-check" @click="resolveBacklog(scope.row)"></el-button>
          </template>
        </el-table-column>
      </el-table>
    </section>

    <section class="ops-table">
      <header>
        <h2>{{ $t('수수료 매핑') }}</h2>
      </header>
      <el-table :data="mappings" size="small" border :empty-text="$t('데이타 없음')" max-height="260">
        <el-table-column prop="platform" :label="$t('플랫폼')" width="130"></el-table-column>
        <el-table-column prop="rawLabel" :label="$t('원본 라벨')" min-width="220"></el-table-column>
        <el-table-column prop="standardCategory" :label="$t('표준 항목')" min-width="160"></el-table-column>
        <el-table-column prop="activeYn" :label="$t('사용')" width="80"></el-table-column>
      </el-table>
    </section>

    <section class="ops-table">
      <header>
        <h2>{{ $t('동기화 상태') }}</h2>
      </header>
      <el-table :data="checkpoints" size="small" border :empty-text="$t('데이타 없음')" max-height="260">
        <el-table-column prop="jobName" :label="$t('작업')" min-width="150"></el-table-column>
        <el-table-column prop="status" :label="$t('상태')" width="110"></el-table-column>
        <el-table-column prop="cursorFormation" :label="$t('재시작 커서')" min-width="180"></el-table-column>
        <el-table-column prop="processedCount" :label="$t('성공')" width="90" align="right"></el-table-column>
        <el-table-column prop="failedCount" :label="$t('실패')" width="90" align="right"></el-table-column>
        <el-table-column prop="lastError" :label="$t('오류')" min-width="180"></el-table-column>
      </el-table>
    </section>
  </div>
</template>

<script>
import StorePicker from '@/components/store/StorePicker.vue'
import mainVueMixin from '@/mixins/mainVueMixin'
import { util } from '@/mixins/utilMixin'
import { DELIVERY_PLATFORM_GROUPS, platformDisplayName } from '@/utils/deliveryPlatform'

export default {
  mixins: [mainVueMixin],
  components: { StorePicker },

  data() {
    return {
      running: false,
      deliveryPlatformGroups: DELIVERY_PLATFORM_GROUPS,
      loading: false,
      errorMessage: '',
      backfill: {
        storeCd: null,
        limit: 100,
      },
      backfillRange: [util.getDate('yyyy-MM-dd', util.dateAdd('m ', -3, new Date())), util.getNow('yyyy-MM-dd')],
      lastResult: '',
      backlog: [],
      mappings: [],
      checkpoints: [],
      resolveCategories: {},
      standardCategories: [
        'DELIVERY_FEE',
        'SERVICE_COMMISSION',
        'CARD_COMMISSION',
        'PLATFORM_DISCOUNT',
        'STORE_DISCOUNT',
        'VAT',
        'AD_COST',
        'INSTANT_DISCOUNT',
      ],
      mappingForm: {
        platform: 'BAEMIN',
        rawLabel: '',
        standardCategory: 'SERVICE_COMMISSION',
      },
    }
  },
  created() {
    this.loadOps()
  },
  methods: {
    platformDisplayName,
    async loadOps() {
      this.loading = true
      this.errorMessage = ''
      try {
        // 셋을 차례로 읽고 하나라도 못 읽으면 멈춘다 — 요청 실패는 헬퍼가 값으로 알리고, 아래 catch 한 곳이 안내한다
        const backlog = await this.mainVue.$axios.get('/api/admin/integrated-sales/backlog', (result) => {
          this.backlog = result || []
          this.resolveCategories = this.backlog.reduce((acc, row) => {
            acc[row.id] = acc[row.id] || 'SERVICE_COMMISSION'
            return acc
          }, this.resolveCategories)
        })
        if (!backlog.ok) throw backlog.error
        const mappings = await this.mainVue.$axios.get('/api/admin/integrated-sales/fee-mapping', (result) => {
          this.mappings = result || []
        })
        if (!mappings.ok) throw mappings.error
        const checkpoints = await this.mainVue.$axios.get('/api/admin/integrated-sales/sync/status', (result) => {
          this.checkpoints = result || []
        })
        if (!checkpoints.ok) throw checkpoints.error
      } catch (error) {
        this.errorMessage = this.$t('운영 데이터를 불러오지 못했습니다. 잠시 후 다시 시도해주세요.')
      } finally {
        this.loading = false
      }
    },
    async runBackfill() {
      if (!this.backfill.storeCd) {
        this.$message.warning(this.$t('매장 코드를 입력해주세요.'))
        return
      }
      this.running = true
      try {
        const request = {
          ...this.backfill,
          startDate: this.backfillRange?.[0],
          endDate: this.backfillRange?.[1],
        }
        const started = await this.mainVue.$axios.post('/api/admin/integrated-sales/backfill', request, (result) => {
          this.lastResult = JSON.stringify(result, null, 2)
        })
        if (!started.ok) throw started.error
        await this.loadOps()
      } catch (error) {
        this.errorMessage = this.$t('백필 실행에 실패했습니다. 입력값과 권한을 확인해주세요.')
      } finally {
        this.running = false
      }
    },
    async evictCache() {
      this.errorMessage = ''
      try {
        const evicted = await this.mainVue.$axios.del('/api/admin/integrated-sales/cache', () => {
          this.$message.success(this.$t('처리되었습니다.'))
        })
        if (!evicted.ok) throw evicted.error
      } catch (error) {
        this.errorMessage = this.$t('캐시 무효화에 실패했습니다. 권한과 서버 상태를 확인해주세요.')
      }
    },
    async createMapping() {
      if (!this.mappingForm.platform || !this.mappingForm.rawLabel || !this.mappingForm.standardCategory) {
        this.$message.warning(this.$t('필수 값을 입력해주세요.'))
        return
      }
      this.errorMessage = ''
      try {
        const created = await this.mainVue.$axios.post(
          '/api/admin/integrated-sales/fee-mapping',
          this.mappingForm,
          () => {
            this.mappingForm.rawLabel = ''
            this.$message.success(this.$t('처리되었습니다.'))
          }
        )
        if (!created.ok) throw created.error
        await this.loadOps()
      } catch (error) {
        this.errorMessage = this.$t('수수료 매핑 등록에 실패했습니다. 입력값과 권한을 확인해주세요.')
      }
    },
    async resolveBacklog(row) {
      this.errorMessage = ''
      try {
        const resolved = await this.mainVue.$axios.patch(
          `/api/admin/integrated-sales/backlog/${row.id}/resolve`,
          { standardCategory: this.resolveCategories[row.id] || 'SERVICE_COMMISSION' },
          {},
          () => {
            this.$message.success(this.$t('처리되었습니다.'))
          }
        )
        if (!resolved.ok) throw resolved.error
        await this.loadOps()
      } catch (error) {
        this.errorMessage = this.$t('BACKLOG 처리에 실패했습니다. 권한과 서버 상태를 확인해주세요.')
      }
    },
  },
}
</script>

<style scoped>
.settlement-ops {
  min-height: calc(100vh - 120px);
  padding: 16px;
  background: var(--color-bg-page);
}

.page-bar,
.ops-table header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
}

h1,
h2 {
  margin: 0;
  color: var(--color-text-primary);
  font-weight: 700;
}

h1 {
  font-size: 18px;
}

h2 {
  font-size: 15px;
}

.ops-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(260px, 360px) minmax(260px, 360px);
  gap: 12px;
  margin-bottom: 12px;
}

.ops-panel,
.ops-table {
  min-width: 0;
  padding: 16px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-card);
  overflow: hidden;
  box-shadow: var(--shadow-card);
}

.state-alert {
  margin-bottom: 12px;
}

.state-panel {
  display: flex;
  min-height: 120px;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 12px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-card);
  color: var(--color-text-regular);
}

.ops-panel h2 {
  margin-bottom: 12px;
}

pre {
  margin: 12px 0 0;
  padding: 12px;
  border-radius: var(--radius-sm);
  background: var(--color-bg-page);
  color: var(--color-text-regular);
  overflow: auto;
}

.ops-table + .ops-table {
  margin-top: 12px;
}

@media (max-width: 768px) {
  .settlement-ops {
    padding: 10px;
  }

  .ops-grid {
    grid-template-columns: 1fr;
  }

  .ops-panel .el-input-number,
  .ops-panel .el-date-editor,
  .ops-panel .el-select,
  .ops-panel .el-button {
    width: 100%;
    max-width: 100%;
  }
}
</style>
